<template>
  <vue-final-modal
    v-model="state"
    classes="modal-overlay-container"
    content-class="modern-modal-content domain-modal-wide"
    @before-close="closeDialog"
  >
    <!-- Modal Header -->
    <div class="modal-header">
      <div class="header-text-container">
        <div class="title-wrapper">
          <div class="title-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="2" y1="12" x2="22" y2="12"></line>
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
            </svg>
          </div>
          <h5 class="modal-title">Phishing Domains &amp; Health</h5>
        </div>
      </div>
      <button type="button" class="close-button" @click="closeDialog" aria-label="Close">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    </div>

    <!-- Modal Body -->
    <div class="modal-body custom-scroll">
      <!-- Segmented View Toggle -->
      <div class="setting-item mb-4">
        <div class="type-toggle-container">
          <button
            type="button"
            class="type-btn"
            :class="{ active: currentTab === 'manage' }"
            @click="currentTab = 'manage'"
          >
            <i class="fas fa-network-wired me-2"></i> Manage Domains
          </button>
          <button
            type="button"
            class="type-btn"
            :class="{ active: currentTab === 'stats' }"
            @click="switchToStats"
          >
            <i class="fas fa-chart-line me-2"></i> Delivery Stats &amp; Logs
          </button>
        </div>
      </div>

      <!-- TAB 1: MANAGE DOMAINS -->
      <div v-if="currentTab === 'manage'">
        <!-- Diagnostic Result Banner -->
        <div
          v-if="diagnosticResult"
          class="diagnostic-banner mb-4"
          :class="isDiagnosticHealthy ? 'banner-healthy' : 'banner-issues'"
        >
          <div class="diagnostic-banner-header d-flex justify-content-between align-items-center mb-3">
            <div class="d-flex align-items-center gap-2">
              <span class="fw-bold text-dark">DKIM &amp; DNS Diagnostics:</span>
              <span class="badge bg-light text-dark border px-2 py-1">{{ diagnosticResult.domain }}</span>
              <span
                class="badge"
                :class="isDiagnosticHealthy ? 'bg-success' : 'bg-danger'"
              >
                <i :class="isDiagnosticHealthy ? 'fas fa-check-circle me-1' : 'fas fa-exclamation-triangle me-1'"></i>
                {{ isDiagnosticHealthy ? 'Healthy' : 'Configuration / DNS Issue' }}
              </span>
            </div>
            <button
              type="button"
              class="btn-close btn-close-sm"
              aria-label="Close"
              @click="diagnosticResult = null"
            ></button>
          </div>

          <!-- 3 Step Pills Grid -->
          <div class="row g-2">
            <!-- Step 1: DNS / Domain Resolution -->
            <div class="col-md-4">
              <div
                class="step-pill-card p-3 rounded h-100"
                :class="diagnosticResult.verification.dns_resolved ? 'step-pass' : 'step-fail'"
              >
                <div class="d-flex align-items-center justify-content-between mb-1">
                  <span class="step-title fw-bold">1. DNS Resolution</span>
                  <i
                    :class="diagnosticResult.verification.dns_resolved ? 'fas fa-check-circle text-success' : 'fas fa-times-circle text-danger'"
                  ></i>
                </div>
                <div class="step-desc small text-muted">
                  {{ diagnosticResult.verification.dns_message || (diagnosticResult.verification.dns_resolved ? 'Domain resolves correctly' : 'DNS resolution failed') }}
                </div>
              </div>
            </div>

            <!-- Step 2: DKIM TXT Record -->
            <div class="col-md-4">
              <div
                class="step-pill-card p-3 rounded h-100"
                :class="diagnosticResult.verification.dkim_record_found ? 'step-pass' : 'step-fail'"
              >
                <div class="d-flex align-items-center justify-content-between mb-1">
                  <span class="step-title fw-bold">2. DKIM TXT Record</span>
                  <i
                    :class="diagnosticResult.verification.dkim_record_found ? 'fas fa-check-circle text-success' : 'fas fa-times-circle text-danger'"
                  ></i>
                </div>
                <div class="step-desc small text-muted">
                  {{ diagnosticResult.verification.dkim_message || (diagnosticResult.verification.dkim_record_found ? 'DKIM TXT record found' : 'DKIM TXT record not found') }}
                </div>
              </div>
            </div>

            <!-- Step 3: RSA Key Pair Match -->
            <div class="col-md-4">
              <div
                class="step-pill-card p-3 rounded h-100"
                :class="diagnosticResult.verification.key_pair_valid ? 'step-pass' : 'step-fail'"
              >
                <div class="d-flex align-items-center justify-content-between mb-1">
                  <span class="step-title fw-bold">3. RSA Key Match</span>
                  <i
                    :class="diagnosticResult.verification.key_pair_valid ? 'fas fa-check-circle text-success' : 'fas fa-times-circle text-danger'"
                  ></i>
                </div>
                <div class="step-desc small text-muted">
                  {{ diagnosticResult.verification.key_message || (diagnosticResult.verification.key_pair_valid ? 'Key pair matches correctly' : 'Key pair invalid or mismatch') }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Section: Configured Domains Table -->
        <div class="domain-section mb-4">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h6 class="section-title mb-0">Configured Phishing Domains</h6>
            <span class="badge bg-light text-secondary border">{{ domains.length }} Registered</span>
          </div>

          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-info" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>

          <div v-else-if="domains.length === 0" class="text-center py-4 bg-light rounded border">
            <i class="fas fa-globe text-muted fa-2x mb-2"></i>
            <p class="text-muted mb-0 small">No domains registered yet. Fill out the form below to configure a domain.</p>
          </div>

          <div v-else class="table-responsive border rounded bg-white">
            <table class="table modern-table mb-0 align-middle">
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>IP Address</th>
                  <th>Selector</th>
                  <th>Keys</th>
                  <th>Health / Last Sent</th>
                  <th class="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in domains" :key="index">
                  <td class="fw-bold text-dark">
                    <i class="fas fa-globe text-muted me-1 small"></i>
                    {{ item.name }}
                  </td>
                  <td class="text-secondary small">{{ item.ip_address || '-' }}</td>
                  <td>
                    <span class="badge bg-light text-dark border">{{ item.selector || 'default' }}</span>
                  </td>
                  <td>
                    <span
                      v-if="item.private_key"
                      class="badge bg-light text-secondary border me-1"
                      :title="shortenedName(item.private_key)"
                    >
                      <i class="fas fa-key text-primary me-1"></i>Priv
                    </span>
                    <span
                      v-if="item.public_key"
                      class="badge bg-light text-secondary border"
                      :title="shortenedName(item.public_key)"
                    >
                      <i class="fas fa-lock text-info me-1"></i>Pub
                    </span>
                    <span v-if="!item.private_key && !item.public_key" class="text-muted small">-</span>
                  </td>
                  <td>
                    <span class="badge bg-success-soft text-success">
                      <i class="fas fa-check-circle me-1"></i>Active
                    </span>
                  </td>
                  <td class="text-end">
                    <div class="d-inline-flex gap-1 align-items-center">
                      <!-- Verify DKIM Button -->
                      <button
                        type="button"
                        class="btn-action-icon btn-action-verify"
                        v-tooltip.top="'Test Domain & DKIM Config'"
                        @click="checkDomainById(item)"
                        :disabled="checkingDkim && checkingDomainId === item.ID"
                      >
                        <span
                          v-if="checkingDkim && checkingDomainId === item.ID"
                          class="spinner-border spinner-border-sm"
                          role="status"
                        ></span>
                        <i v-else class="fas fa-shield-alt"></i>
                      </button>

                      <!-- Edit Button -->
                      <button
                        type="button"
                        class="btn-action-icon btn-action-edit"
                        v-tooltip.top="'Edit Domain'"
                        @click="setDomain(item)"
                      >
                        <i class="far fa-edit"></i>
                      </button>

                      <!-- Delete Button -->
                      <button
                        type="button"
                        class="btn-action-icon btn-action-delete"
                        v-tooltip.top="'Delete Domain'"
                        @click="deleteDomain(item.ID)"
                      >
                        <i class="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <hr class="my-4" style="border-color: #f1f5f9;" />

        <!-- Section: Add / Update Domain Form -->
        <div class="domain-form-section">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h6 class="section-title mb-0">
                {{ buttonSave === 'Save' ? 'Add New Domain' : `Update Domain: ${editedItem.name}` }}
              </h6>
              <small class="text-muted">Enter network parameters and RSA cryptographic keys for email signing.</small>
            </div>
            <button
              v-if="buttonSave !== 'Save'"
              type="button"
              class="btn btn-sm btn-outline-secondary"
              @click="clearFields"
            >
              <i class="fas fa-plus me-1"></i> Switch to Add New
            </button>
          </div>

          <div class="row g-3">
            <!-- Domain Name -->
            <div class="col-md-6">
              <div class="setting-item mb-0">
                <label for="domainName">Domain Name <span class="text-danger">*</span></label>
                <div class="input-with-icon">
                  <div class="input-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="2" y1="12" x2="22" y2="12"></line>
                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                    </svg>
                  </div>
                  <input
                    id="domainName"
                    v-model="editedItem.name"
                    type="text"
                    class="form-control"
                    :class="{ 'border-danger': v$.editedItem.name.$error }"
                    placeholder="e.g. mail.acme-security.com"
                    @blur="v$.editedItem.name.$touch()"
                  />
                </div>
                <div class="text-danger mt-1 ms-1" v-for="error of v$.editedItem.name.$errors" :key="error.$uid">
                  <div style="font-size: 0.75rem; font-weight: 600;">{{ error.$message }}</div>
                </div>
              </div>
            </div>

            <!-- IP Address -->
            <div class="col-md-6">
              <div class="setting-item mb-0">
                <label for="ipAddress">IP Address <span class="text-danger">*</span></label>
                <div class="input-with-icon">
                  <div class="input-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <rect x="2" y="2" width="20" height="8" rx="2" ry="2"></rect>
                      <rect x="2" y="14" width="20" height="8" rx="2" ry="2"></rect>
                      <line x1="6" y1="6" x2="6.01" y2="6"></line>
                      <line x1="6" y1="18" x2="6.01" y2="18"></line>
                    </svg>
                  </div>
                  <input
                    id="ipAddress"
                    v-model="editedItem.ip_address"
                    type="text"
                    class="form-control"
                    :class="{ 'border-danger': v$.editedItem.ip_address.$error }"
                    placeholder="e.g. 192.168.1.10"
                    @blur="v$.editedItem.ip_address.$touch()"
                  />
                </div>
                <div class="text-danger mt-1 ms-1" v-for="error of v$.editedItem.ip_address.$errors" :key="error.$uid">
                  <div style="font-size: 0.75rem; font-weight: 600;">{{ error.$message }}</div>
                </div>
              </div>
            </div>

            <!-- DKIM Selector -->
            <div class="col-md-12">
              <div class="setting-item mb-0">
                <label for="dkimSelector">DKIM Selector</label>
                <div class="input-with-icon">
                  <div class="input-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                      <line x1="7" y1="7" x2="7.01" y2="7"></line>
                    </svg>
                  </div>
                  <input
                    id="dkimSelector"
                    v-model="editedItem.selector"
                    type="text"
                    class="form-control"
                    placeholder="e.g. default or mail"
                  />
                </div>
              </div>
            </div>

            <!-- Private Key -->
            <div class="col-md-6">
              <div class="setting-item mb-0">
                <label for="privateKey">Private Key</label>
                <div class="input-with-icon">
                  <div class="input-icon align-self-start mt-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M21 2l-2 2m-1.5 1.5L14 9l-2-2-4 4 2 2-4 4-2-2-4 4"></path>
                      <circle cx="7.5" cy="16.5" r="4.5"></circle>
                    </svg>
                  </div>
                  <textarea
                    id="privateKey"
                    v-model="editedItem.private_key"
                    rows="3"
                    class="form-control code-input"
                    placeholder="-----BEGIN RSA PRIVATE KEY-----"
                  ></textarea>
                </div>
              </div>
            </div>

            <!-- Public Key -->
            <div class="col-md-6">
              <div class="setting-item mb-0">
                <label for="publicKey">Public Key</label>
                <div class="input-with-icon">
                  <div class="input-icon align-self-start mt-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                      <path d="M7 11V7a5 5 0 0 1 9.9-1"></path>
                    </svg>
                  </div>
                  <textarea
                    id="publicKey"
                    v-model="editedItem.public_key"
                    rows="3"
                    class="form-control code-input"
                    placeholder="-----BEGIN PUBLIC KEY----- (or base64 TXT string)"
                  ></textarea>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- TAB 2: DELIVERY STATS & LOGS -->
      <div v-else-if="currentTab === 'stats'">
        <div class="stats-overview mb-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h6 class="section-title mb-0">Domain Delivery Performance</h6>
              <small class="text-muted">Metrics and status records aggregated by sending domain.</small>
            </div>
            <div class="d-flex align-items-center gap-2">
              <span
                v-if="selectedDomainFilter !== 'all'"
                class="badge bg-primary text-white d-flex align-items-center gap-1"
              >
                Filtered: {{ selectedDomainFilter }}
                <i
                  class="fas fa-times ms-1"
                  style="cursor: pointer;"
                  @click="clearDomainFilter"
                  title="Clear domain filter"
                ></i>
              </span>
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary d-flex align-items-center"
                @click="getStats"
                :disabled="statsLoading"
              >
                <span v-if="statsLoading" class="spinner-border spinner-border-sm me-1" role="status"></span>
                <i v-else class="fas fa-sync-alt me-1"></i>
                Refresh
              </button>
            </div>
          </div>

          <div v-if="statsLoading" class="text-center py-4">
            <div class="spinner-border text-info" role="status">
              <span class="visually-hidden">Loading Stats...</span>
            </div>
          </div>

          <div v-else-if="computedDomainStats.length === 0" class="text-center py-4 bg-light rounded border">
            <i class="fas fa-chart-bar text-muted fa-2x mb-2"></i>
            <p class="text-muted mb-0 small">No delivery statistics recorded yet. Dispatch campaigns to view telemetry.</p>
          </div>

          <!-- Per-Domain Summary Table -->
          <div v-else class="table-responsive border rounded bg-white mb-4">
            <table class="table modern-table mb-0 align-middle">
              <thead>
                <tr>
                  <th>Domain Name</th>
                  <th>Templates</th>
                  <th>Sent</th>
                  <th>Scheduled</th>
                  <th>Critical Failed</th>
                  <th>Expired</th>
                  <th>Success Rate</th>
                  <th>Last Sent At</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(stat, idx) in computedDomainStats"
                  :key="idx"
                  class="clickable-row"
                  :class="{ 'row-selected': selectedDomainFilter.toLowerCase() === getDomainName(stat).toLowerCase() }"
                  @click="toggleDomainFilter(getDomainName(stat))"
                  title="Click to filter logs to this domain"
                >
                  <td class="fw-bold text-dark">
                    <i class="fas fa-globe text-muted me-1 small"></i>
                    {{ getDomainName(stat) }}
                  </td>
                  <td>{{ stat.templates_count ?? stat.active_templates_count ?? stat.templates ?? 0 }}</td>
                  <td>
                    <span class="badge bg-success-soft text-success">
                      {{ stat.sent_count || stat.sent || 0 }}
                    </span>
                  </td>
                  <td>
                    <span class="badge bg-info-soft text-primary">
                      {{ stat.scheduled_count || stat.scheduled || 0 }}
                    </span>
                  </td>
                  <td>
                    <span class="badge bg-danger-soft text-danger">
                      {{ stat.critical_failed_count ?? stat.failed_count ?? stat.critical_failed ?? stat.failed ?? 0 }}
                    </span>
                  </td>
                  <td>
                    <span class="badge bg-secondary-soft text-secondary">
                      {{ stat.expired_count || stat.expired || 0 }}
                    </span>
                  </td>
                  <td>
                    <span class="fw-bold" :class="getSuccessRateNumber(stat) >= 80 ? 'text-success' : 'text-danger'">
                      {{ getSuccessRate(stat) }}
                    </span>
                  </td>
                  <td class="small text-muted">{{ formatDate(stat.last_sent_at || stat.last_sent) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Error & Delivery Logs History -->
          <div class="logs-section">
            <div class="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
              <div>
                <h6 class="section-title mb-0">Delivery &amp; Error History</h6>
                <small class="text-muted">Real-time trace logs and SMTP transmission status.</small>
              </div>

              <div class="d-flex flex-wrap align-items-center gap-3">
                <div class="d-flex flex-row align-items-center">
                  <span class="p-2" style="color: #435666; font-size: 13px; font-weight: 500;">
                    <i class="fa-solid fa-list-ol me-1"></i> Items per page
                  </span>
                  <select
                    v-model="logsPagination.pageSize"
                    @change="handleLogsPageSizeChange"
                    class="form-select shadow-none"
                    style="width: auto; border: 1px solid #00A0C1; border-radius: 20px; color: #435666; height: 32px; padding: 0.1rem 2.2rem 0.1rem 1rem; font-size: 13px;"
                  >
                    <option v-for="row in rowsPerPageList" :value="row.value" :key="row.value">{{ row.text }}</option>
                  </select>
                </div>

                <!-- Filter Pills -->
                <div class="filter-pills-container">
                  <button
                    type="button"
                    class="filter-pill-btn"
                    :class="{ active: selectedLogFilter === 'all' }"
                    @click="setLogFilter('all')"
                  >
                    All Logs
                  </button>
                  <button
                    type="button"
                    class="filter-pill-btn"
                    :class="{ active: selectedLogFilter === 'critical' }"
                    @click="setLogFilter('critical')"
                  >
                    <i class="fas fa-exclamation-circle text-danger me-1"></i> Critical Errors Only
                  </button>
                  <button
                    type="button"
                    class="filter-pill-btn"
                    :class="{ active: selectedLogFilter === 'sent' }"
                    @click="setLogFilter('sent')"
                  >
                    <i class="fas fa-check-circle text-success me-1"></i> Sent
                  </button>
                  <button
                    type="button"
                    class="filter-pill-btn"
                    :class="{ active: selectedLogFilter === 'expired' }"
                    @click="setLogFilter('expired')"
                  >
                    <i class="fas fa-clock text-secondary me-1"></i> Expired
                  </button>
                </div>
              </div>
            </div>

            <div v-if="filteredLogs.length === 0" class="text-center py-4 bg-light rounded border">
              <i class="fas fa-inbox text-muted fa-2x mb-2"></i>
              <p class="text-muted mb-0 small">No logs match the selected filter criteria.</p>
            </div>

            <div v-else class="table-responsive border rounded bg-white">
              <table class="table modern-table mb-0 align-middle">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Organisation</th>
                    <th>Domain &amp; Template</th>
                    <th>Recipient</th>
                    <th>Status</th>
                    <th>Error / Status Details</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(log, lIdx) in filteredLogs" :key="lIdx">
                    <td class="small text-muted text-nowrap">
                      {{ formatDate(log.updated_at || log.date_sent || log.timestamp || log.created_at) }}
                    </td>
                    <td class="small text-secondary">{{ log.organisation_name || log.organisation || '-' }}</td>
                    <td>
                      <div class="fw-bold small text-dark">{{ log.domain_name || log.domain || '-' }}</div>
                      <div class="text-muted" style="font-size: 0.75rem;">{{ log.template_name || log.template || '-' }}</div>
                    </td>
                    <td class="small font-monospace text-secondary">{{ log.recipient || log.email || '-' }}</td>
                    <td>
                      <span class="badge" :class="getLogBadgeClass(log.severity || log.status)">
                        {{ (log.severity ? log.severity.toUpperCase() : null) || log.status || 'Unknown' }}
                      </span>
                    </td>
                    <td class="small text-secondary" style="max-width: 250px; word-break: break-word;">
                      {{ log.status_text || log.error_details || log.message || '-' }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Bottom Pagination Bar -->
            <div class="text-center mt-3 mb-1" v-if="!statsLoading && logsPagination.totalItems > 0">
              <div class="d-inline pagination">
                <a
                  @click="handleLogsPageChange(logsPagination.page - 1)"
                  class="pagination-arrow"
                  :class="{ 'disabled': logsPagination.page <= 1 }"
                >
                  <i class="fa fa-chevron-left"></i>
                </a>
                <span class="pagination-label">
                  <strong>
                    <span>
                      {{ ((logsPagination.page - 1) * logsPagination.pageSize) + 1 }} - {{ Math.min(logsPagination.page * logsPagination.pageSize, logsPagination.totalItems) }} of {{ logsPagination.totalItems }}
                    </span>
                  </strong>
                </span>
                <a
                  @click="handleLogsPageChange(logsPagination.page + 1)"
                  class="pagination-arrow"
                  :class="{ 'disabled': logsPagination.page >= logsPagination.totalPages || logsPagination.totalItems === 0 }"
                >
                  <i class="fa fa-chevron-right"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Footer -->
    <div class="modal-footer">
      <template v-if="currentTab === 'manage'">
        <button type="button" class="btn btn-secondary" @click="clearFields">
          {{ buttonSave === 'Save' ? 'Clear' : 'Cancel Edit' }}
        </button>

        <button
          type="button"
          class="btn btn-outline-info d-flex align-items-center"
          @click="testFormConfig"
          :disabled="testingFormConfig || !editedItem.name"
        >
          <span v-if="testingFormConfig" class="spinner-border spinner-border-sm me-2" role="status"></span>
          <i v-else class="fas fa-vial me-2"></i>
          Test Config
        </button>

        <button
          type="button"
          class="btn btn-primary d-flex align-items-center"
          @click="addNewDomain"
          :disabled="savingDomain"
        >
          <span v-if="savingDomain" class="spinner-border spinner-border-sm me-2" role="status"></span>
          <i v-else class="fas fa-check me-2"></i>
          {{ buttonSave === 'Save' ? 'Save Domain' : 'Update Domain' }}
        </button>
      </template>

      <template v-else-if="currentTab === 'stats'">
        <button type="button" class="btn btn-secondary" @click="closeDialog">
          Close
        </button>
        <button
          type="button"
          class="btn btn-primary d-flex align-items-center"
          @click="getStats"
          :disabled="statsLoading"
        >
          <span v-if="statsLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
          <i v-else class="fas fa-sync-alt me-2"></i>
          Refresh Stats &amp; Logs
        </button>
      </template>
    </div>
  </vue-final-modal>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required, ipAddress } from '@vuelidate/validators'
import moment from 'moment'

export default {
  name: "PhishingDomainModal",

  props: {
    dialog: {
      type: Boolean,
      required: true,
    },
  },

  setup() {
    return { v$: useVuelidate() }
  },

  validations() {
    return {
      editedItem: {
        name: { required },
        ip_address: { ipAddress }
      },
    }
  },

  data() {
    return {
      state: this.dialog,
      currentTab: 'manage', // 'manage' | 'stats'
      buttonSave: 'Save',
      loading: true,
      savingDomain: false,
      testingFormConfig: false,
      checkingDkim: false,
      checkingDomainId: null,
      statsLoading: false,
      selectedDomainFilter: 'all',
      selectedLogFilter: 'all', // 'all' | 'critical' | 'sent' | 'expired'
      logsPagination: {
        page: 1,
        pageSize: 10,
        totalItems: 0,
        totalPages: 1,
      },
      rowsPerPageList: [
        { value: 10, text: '10' },
        { value: 25, text: '25' },
        { value: 50, text: '50' },
        { value: 100, text: '100' },
      ],
      diagnosticResult: null,
      editedItem: {
        ID: 0,
        name: "",
        private_key: "",
        public_key: "",
        selector: "",
        ip_address: "",
      },
      domains: [],
      domainStats: [],
      deliveryLogs: [],
      orgID: this.$helper.setOrganisationID(),
    };
  },

  computed: {
    isDiagnosticHealthy() {
      if (!this.diagnosticResult || !this.diagnosticResult.verification) return false;
      const v = this.diagnosticResult.verification;
      if (typeof v.healthy === 'boolean') return v.healthy;
      if (typeof v.overall_healthy === 'boolean') return v.overall_healthy;
      return Boolean(v.dns_resolved && v.dkim_record_found && v.key_pair_valid);
    },

    computedDomainStats() {
      if (this.domainStats && this.domainStats.length > 0) {
        return this.domainStats.map(stat => ({
          ...stat,
          templates_count: stat.templates_count ?? stat.active_templates_count ?? stat.templates ?? 0,
          critical_failed_count: stat.critical_failed_count ?? stat.failed_count ?? stat.failed ?? stat.critical_failed ?? 0
        }));
      }
      return this.domains.map(d => ({
        domain_name: d.name,
        templates_count: 0,
        sent_count: 0,
        scheduled_count: 0,
        critical_failed_count: 0,
        expired_count: 0,
        success_rate: '100%',
        last_sent_at: null
      }));
    },

    filteredLogs() {
      return this.deliveryLogs.filter(log => {
        // Filter by Domain
        if (this.selectedDomainFilter !== 'all') {
          const logDomain = (log.domain_name || log.domain || '').toLowerCase();
          if (logDomain !== this.selectedDomainFilter.toLowerCase()) {
            return false;
          }
        }

        // Filter by Status / Severity
        const status = ((log.status || '') + ' ' + (log.severity || '') + ' ' + (log.status_text || '')).toLowerCase();
        if (this.selectedLogFilter === 'critical') {
          return status.includes('crit') || status.includes('fail') || status.includes('err');
        }
        if (this.selectedLogFilter === 'sent') {
          return status.includes('sent') || status.includes('success');
        }
        if (this.selectedLogFilter === 'expired') {
          return status.includes('expir');
        }

        return true;
      });
    }
  },

  watch: {
    dialog(newVal) {
      this.state = newVal;
      if (newVal) {
        this.getData();
        if (this.currentTab === 'stats') {
          this.getStats();
        }
      }
    },

    organisation() {
      if (this.$store.state.auth.accessToken == null) return;
      this.orgID = this.$store.state.user.organisation.id;
      this.getData();
    }
  },

  async mounted() {
    await this.getData();
  },

  methods: {
    formatToastMessage(msg, fallback = '') {
      const text = String(msg || fallback || '').trim();
      if (!text) return '';
      return /[.!?]$/.test(text) ? text : `${text}.`;
    },

    async getData() {
      this.loading = true;
      try {
        const response = await this.$axios.get('/supadmin/phishing-domain');
        if (response.data && response.data.success) {
          this.domains = response.data.domains || [];
        } else {
          this.$toast.error(this.formatToastMessage(response.data ? response.data.message : '', 'Failed to fetch domains.'));
        }
      } catch (error) {
        const errorMessage = this.$helper ? this.$helper.getErrorMessage(error) : (error.message || 'Error fetching domains.');
        this.$toast.error(this.formatToastMessage(errorMessage, 'Error fetching domains.'));
      } finally {
        this.loading = false;
      }
    },

    async getStats() {
      this.statsLoading = true;
      try {
        const params = {
          page: this.logsPagination.page,
          limit: this.logsPagination.pageSize,
        };
        if (this.selectedDomainFilter && this.selectedDomainFilter !== 'all') {
          params.domain = this.selectedDomainFilter;
        }
        if (this.selectedLogFilter && this.selectedLogFilter !== 'all') {
          params.log_filter = this.selectedLogFilter;
        }

        const response = await this.$axios.get('/supadmin/phishing-domain/stats', { params });
        const payload = response.data || {};
        if (payload.success) {
          this.domainStats = payload.stats || payload.domains || [];
          this.deliveryLogs = payload.logs || payload.delivery_logs || payload.history || [];
        } else {
          this.domainStats = payload.stats || [];
          this.deliveryLogs = payload.logs || [];
        }

        const pag = payload.pagination || {};
        const total = pag.total_items ?? pag.total ?? payload.total_logs ?? payload.total ?? this.deliveryLogs.length;
        this.logsPagination.totalItems = Number(total) || 0;
        this.logsPagination.totalPages = pag.total_pages ?? payload.total_pages ?? (Math.ceil(this.logsPagination.totalItems / this.logsPagination.pageSize) || 1);
      } catch (error) {
        // Fallback gracefully
        this.domainStats = [];
        this.deliveryLogs = [];
        this.logsPagination.totalItems = 0;
        this.logsPagination.totalPages = 1;
      } finally {
        this.statsLoading = false;
      }
    },

    switchToStats() {
      this.currentTab = 'stats';
      this.getStats();
    },

    async addNewDomain() {
      const isFormValid = await this.v$.editedItem.$validate();
      if (!isFormValid) return;

      this.savingDomain = true;

      try {
        if (this.buttonSave === 'Save') {
          const response = await this.$axios.post('/supadmin/phishing-domain', this.editedItem);
          if (response.data && response.data.success) {
            this.$toast.success(this.formatToastMessage(response.data.message, 'Domain added successfully.'));
            await this.getData();
            this.clearFields();
          } else {
            this.$toast.error(this.formatToastMessage(response.data ? response.data.message : '', 'Failed to add domain.'));
          }
        } else {
          const response = await this.$axios.put('/supadmin/phishing-domain/' + this.editedItem.ID, this.editedItem);
          if (response.data && response.data.success) {
            this.$toast.success(this.formatToastMessage(response.data.message, 'Domain updated successfully.'));
            await this.getData();
            this.clearFields();
          } else {
            this.$toast.error(this.formatToastMessage(response.data ? response.data.message : '', 'Failed to update domain.'));
          }
        }
      } catch (error) {
        const errorMessage = this.$helper ? this.$helper.getErrorMessage(error) : (error.message || 'Error saving domain.');
        this.$toast.error(this.formatToastMessage(errorMessage, 'Error saving domain.'));
      } finally {
        this.savingDomain = false;
        this.v$.$reset();
      }
    },

    async checkDomainById(item) {
      this.checkingDkim = true;
      this.checkingDomainId = item.ID;
      this.diagnosticResult = null;

      try {
        const payload = {
          id: item.ID,
          name: item.name,
          ip_address: item.ip_address,
          selector: item.selector,
          public_key: item.public_key,
          private_key: item.private_key
        };
        const response = await this.$axios.post('/supadmin/phishing-domain/check', payload);
        const result = response.data || {};
        const v = result.verification || result.data || result;
        const isHealthy = typeof v.healthy === 'boolean'
          ? v.healthy
          : Boolean(v.overall_healthy ?? result.overall_healthy ?? (v.dns_resolved && v.dkim_record_found && v.key_pair_valid));

        this.diagnosticResult = {
          domain: item.name,
          verification: {
            healthy: isHealthy,
            overall_healthy: isHealthy,
            dns_resolved: Boolean(v.dns_resolved),
            dns_message: v.dns_message || (v.dns_resolved ? 'Domain resolves correctly.' : 'DNS resolution failed.'),
            dkim_record_found: Boolean(v.dkim_record_found),
            dkim_message: v.dkim_message || (v.dkim_record_found ? `DKIM TXT record found for selector "${item.selector || 'default'}".` : 'DKIM record could not be found.'),
            key_pair_valid: Boolean(v.key_pair_valid),
            key_message: v.key_message || (v.key_pair_valid ? 'Key pair is valid and cryptographic signatures match.' : 'Key pair validation failed.')
          }
        };

        if (result.success !== false) {
          this.$toast.success(this.formatToastMessage(result.message, 'Domain configuration verification completed.'));
        } else {
          this.$toast.error(this.formatToastMessage(result.message, `DKIM check reported issues for ${item.name}.`));
        }
      } catch (error) {
        const msg = this.$helper ? this.$helper.getErrorMessage(error) : (error.message || 'Error checking domain DKIM.');
        this.diagnosticResult = {
          domain: item.name,
          verification: {
            healthy: false,
            overall_healthy: false,
            dns_resolved: false,
            dns_message: msg,
            dkim_record_found: false,
            dkim_message: 'Unable to query DNS DKIM record.',
            key_pair_valid: false,
            key_message: 'Key validation failed.'
          }
        };
        this.$toast.error(this.formatToastMessage(msg, 'Error checking domain DKIM.'));
      } finally {
        this.checkingDkim = false;
        this.checkingDomainId = null;
      }
    },

    async testFormConfig() {
      await this.v$.editedItem.$touch();
      if (this.v$.editedItem.name.$invalid || this.v$.editedItem.ip_address.$invalid) {
        this.$toast.error(this.formatToastMessage('Please enter a valid Domain and IP address before testing.'));
        return;
      }

      this.testingFormConfig = true;
      this.diagnosticResult = null;

      try {
        const payload = {
          id: this.editedItem.ID || 0,
          name: this.editedItem.name,
          ip_address: this.editedItem.ip_address,
          selector: this.editedItem.selector,
          public_key: this.editedItem.public_key,
          private_key: this.editedItem.private_key
        };
        const response = await this.$axios.post('/supadmin/phishing-domain/check', payload);
        const result = response.data || {};
        const v = result.verification || result.data || result;
        const isHealthy = typeof v.healthy === 'boolean'
          ? v.healthy
          : Boolean(v.overall_healthy ?? result.overall_healthy ?? (v.dns_resolved && v.dkim_record_found && v.key_pair_valid));

        this.diagnosticResult = {
          domain: this.editedItem.name,
          verification: {
            healthy: isHealthy,
            overall_healthy: isHealthy,
            dns_resolved: Boolean(v.dns_resolved),
            dns_message: v.dns_message || (v.dns_resolved ? 'Domain resolves correctly.' : 'DNS resolution failed.'),
            dkim_record_found: Boolean(v.dkim_record_found),
            dkim_message: v.dkim_message || (v.dkim_record_found ? `DKIM TXT record found for selector "${this.editedItem.selector || 'default'}".` : 'DKIM record not found.'),
            key_pair_valid: Boolean(v.key_pair_valid),
            key_message: v.key_message || (v.key_pair_valid ? 'Key pair is valid and cryptographic signatures match.' : 'Key pair validation failed.')
          }
        };

        if (result.success !== false) {
          this.$toast.success(this.formatToastMessage(result.message, 'Domain configuration verification completed.'));
        } else {
          this.$toast.error(this.formatToastMessage(result.message, 'Config verification failed. Issues detected.'));
        }
      } catch (error) {
        const msg = this.$helper ? this.$helper.getErrorMessage(error) : (error.message || 'Error testing config.');
        this.diagnosticResult = {
          domain: this.editedItem.name,
          verification: {
            healthy: false,
            overall_healthy: false,
            dns_resolved: false,
            dns_message: msg,
            dkim_record_found: false,
            dkim_message: 'Unable to query DNS DKIM record.',
            key_pair_valid: false,
            key_message: 'Key validation failed.'
          }
        };
        this.$toast.error(this.formatToastMessage(msg, 'Error testing config.'));
      } finally {
        this.testingFormConfig = false;
      }
    },

    setDomain(item) {
      this.editedItem.ID = item.ID;
      this.editedItem.name = item.name;
      this.editedItem.private_key = item.private_key;
      this.editedItem.public_key = item.public_key;
      this.editedItem.selector = item.selector;
      this.editedItem.ip_address = item.ip_address;
      this.buttonSave = "Update";
    },

    clearFields() {
      this.editedItem = {
        ID: 0,
        name: "",
        ip_address: "",
        private_key: "",
        public_key: "",
        selector: "",
      };
      this.buttonSave = "Save";
      this.v$.$reset();
    },

    clear() {
      this.clearFields();
    },

    async deleteDomain(id) {
      if (confirm("Do you really want to delete this domain?")) {
        try {
          const response = await this.$axios.delete('/supadmin/phishing-domain/' + id);
          if (response.data && response.data.success) {
            this.$toast.success(this.formatToastMessage(response.data.message, 'Domain deleted.'));
            await this.getData();
          } else {
            this.$toast.error(this.formatToastMessage(response.data ? response.data.message : '', 'Failed to delete domain.'));
          }
        } catch (error) {
          const errorMessage = this.$helper ? this.$helper.getErrorMessage(error) : (error.message || 'Error deleting domain.');
          this.$toast.error(this.formatToastMessage(errorMessage, 'Error deleting domain.'));
        }
      }
    },

    closeDialog() {
      this.$emit("close");
      this.state = false;
    },

    handleLogsPageChange(newPage) {
      if (newPage >= 1 && newPage <= this.logsPagination.totalPages && newPage !== this.logsPagination.page) {
        this.logsPagination.page = newPage;
        this.getStats();
      }
    },

    handleLogsPageSizeChange() {
      this.logsPagination.page = 1;
      this.getStats();
    },

    setLogFilter(filter) {
      this.selectedLogFilter = filter;
      this.logsPagination.page = 1;
      this.getStats();
    },

    toggleDomainFilter(domainName) {
      if (this.selectedDomainFilter.toLowerCase() === domainName.toLowerCase()) {
        this.selectedDomainFilter = 'all';
      } else {
        this.selectedDomainFilter = domainName;
      }
      this.logsPagination.page = 1;
      this.getStats();
    },

    clearDomainFilter() {
      this.selectedDomainFilter = 'all';
      this.logsPagination.page = 1;
      this.getStats();
    },

    getDomainName(stat) {
      return stat.domain_name || stat.name || stat.domain || 'Unknown';
    },

    getSuccessRate(stat) {
      if (stat.success_rate !== undefined && stat.success_rate !== null) {
        return typeof stat.success_rate === 'number' ? `${stat.success_rate}%` : stat.success_rate;
      }
      const sent = Number(stat.sent_count || stat.sent || 0);
      const failed = Number(stat.critical_failed_count ?? stat.failed_count ?? stat.failed ?? stat.critical_failed ?? 0);
      const total = sent + failed;
      if (total === 0) return '100%';
      return `${Math.round((sent / total) * 100)}%`;
    },

    getSuccessRateNumber(stat) {
      const rateStr = this.getSuccessRate(stat);
      return parseFloat(rateStr) || 100;
    },

    formatDate(val) {
      if (!val || val === 'Never') return 'Never';
      const m = moment(val);
      return m.isValid() ? m.format('MMM DD, YYYY HH:mm') : 'Never';
    },

    shortenedName(item) {
      if (item && item.length > 20) {
        return item.slice(0, 18) + '...';
      }
      return item || '';
    },

    getLogBadgeClass(status) {
      const s = (status || '').toLowerCase();
      if (s.includes('crit') || s.includes('fail') || s.includes('err')) {
        return 'bg-danger-soft text-danger';
      }
      if (s.includes('minor') || s.includes('warn')) {
        return 'bg-warning-soft text-warning';
      }
      if (s.includes('sent') || s.includes('success')) {
        return 'bg-success-soft text-success';
      }
      if (s.includes('sched')) {
        return 'bg-info-soft text-primary';
      }
      if (s.includes('expir')) {
        return 'bg-secondary-soft text-secondary';
      }
      return 'bg-light text-dark border';
    }
  }
};
</script>

<style scoped>
:deep(.modal-overlay-container) {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(4px);
  z-index: 1000;
}

:deep(.modern-modal-content) {
  background: white;
  border-radius: 12px;
  width: 100%;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  border: none !important;
  padding: 0;
  margin: 0 1rem;
  overflow: hidden;
  max-height: 90vh;
}

:deep(.domain-modal-wide) {
  max-width: 860px !important;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f1f5f9;
  background-color: #ffffff;
}

.title-wrapper {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.title-icon {
  color: #00a0c1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 160, 193, 0.1);
  padding: 8px;
  border-radius: 8px;
}

.modal-title {
  color: #1e293b;
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
}

.close-button {
  background: #f1f5f9;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #94a3b8;
  transition: all 0.2s;
}

.close-button:hover {
  background-color: #fee2e2;
  color: #dc3545;
  transform: rotate(90deg);
}

.modal-body.custom-scroll {
  padding: 1.25rem 1.5rem;
  flex-grow: 1;
  overflow-y: auto;
}

.section-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1e293b;
}

/* Form controls */
.setting-item {
  margin-bottom: 1.25rem;
}

.setting-item label {
  display: block;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  color: #64748b;
  margin-bottom: 0.5rem;
  font-weight: 700;
}

.input-with-icon {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 12px;
  color: #94a3b8;
  display: flex;
  align-items: center;
  pointer-events: none;
  z-index: 1;
}

.form-control {
  width: 100%;
  padding: 0.7rem 0.7rem 0.7rem 2.5rem;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.95rem;
  color: #1e293b;
  background-color: #ffffff;
  transition: all 0.2s;
}

.form-control:focus {
  border-color: #00a0c1;
  outline: none;
  box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.15);
}

.code-input {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 0.8rem;
  line-height: 1.4;
}

/* Segmented Toggle */
.type-toggle-container {
  display: flex;
  background-color: #f1f5f9;
  border-radius: 8px;
  padding: 4px;
  gap: 4px;
}

.type-btn {
  flex: 1;
  border: none;
  background: transparent;
  padding: 0.6rem;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
}

.type-btn:hover:not(:disabled) {
  color: #1e293b;
}

.type-btn.active {
  background-color: white;
  color: #00a0c1;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

/* Diagnostics Banner */
.diagnostic-banner {
  border-radius: 10px;
  padding: 1rem 1.25rem;
  transition: all 0.2s;
}

.banner-healthy {
  background: #f0fdf4;
  border: 1.5px solid #bbf7d0;
}

.banner-issues {
  background: #fef2f2;
  border: 1.5px solid #fecaca;
}

.step-pill-card {
  background: white;
  border: 1px solid #e2e8f0;
  transition: all 0.2s;
}

.step-pill-card.step-pass {
  border-left: 4px solid #10b981;
}

.step-pill-card.step-fail {
  border-left: 4px solid #ef4444;
}

.step-title {
  font-size: 0.82rem;
  color: #1e293b;
}

.step-desc {
  font-size: 0.75rem;
  line-height: 1.3;
}

/* Modern Tables */
.modern-table {
  font-size: 0.85rem;
}

.modern-table thead th {
  background-color: #f8fafc;
  color: #64748b;
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.72rem;
  letter-spacing: 0.03em;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e2e8f0;
}

.modern-table tbody td {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #f1f5f9;
}

.modern-table tbody tr:last-child td {
  border-bottom: none;
}

.clickable-row {
  cursor: pointer;
  transition: background-color 0.15s;
}

.clickable-row:hover {
  background-color: #f8fafc;
}

.row-selected {
  background-color: #f0f9ff !important;
  border-left: 3px solid #00a0c1;
}

/* Soft Badges */
.bg-success-soft {
  background-color: #dcfce7;
  color: #16a34a;
}

.bg-danger-soft {
  background-color: #fee2e2;
  color: #dc2626;
}

.bg-warning-soft {
  background-color: #fef3c7;
  color: #d97706;
}

.bg-info-soft {
  background-color: #e0f2fe;
  color: #0284c7;
}

.bg-secondary-soft {
  background-color: #f1f5f9;
  color: #64748b;
}

/* Action Buttons */
.btn-action-icon {
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  background-color: white;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.15s;
}

.btn-action-verify {
  color: #00a0c1;
}
.btn-action-verify:hover:not(:disabled) {
  background-color: #f0f9ff;
  border-color: #00a0c1;
}

.btn-action-edit {
  color: #3b82f6;
}
.btn-action-edit:hover {
  background-color: #eff6ff;
  border-color: #3b82f6;
}

.btn-action-delete {
  color: #ef4444;
}
.btn-action-delete:hover {
  background-color: #fef2f2;
  border-color: #ef4444;
}

/* Filter Pills */
.filter-pills-container {
  display: flex;
  background-color: #f1f5f9;
  border-radius: 8px;
  padding: 3px;
  gap: 3px;
}

.filter-pill-btn {
  border: none;
  background: transparent;
  padding: 0.35rem 0.75rem;
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s;
}

.filter-pill-btn:hover {
  color: #1e293b;
}

.filter-pill-btn.active {
  background-color: white;
  color: #00a0c1;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

/* Footer & Buttons */
.modal-footer {
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #f1f5f9;
  padding: 1rem 1.5rem;
  gap: 0.75rem;
  background-color: #f8fafc;
}

.modal-footer .btn {
  padding: 0.5rem 1.25rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid transparent;
}

.modal-footer .btn-primary {
  background-color: #00a0c1;
  color: white;
  border-color: #00a0c1;
}

.modal-footer .btn-primary:hover:not(:disabled) {
  background-color: #008da8;
  border-color: #008da8;
}

.modal-footer .btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.modal-footer .btn-secondary {
  background-color: white;
  color: #64748b;
  border: 1px solid #e2e8f0;
}

.modal-footer .btn-secondary:hover {
  background-color: #f8fafc;
  border-color: #cbd5e1;
  color: #1e293b;
}

.modal-footer .btn-outline-info {
  background-color: white;
  color: #00a0c1;
  border: 1px solid #00a0c1;
}

.modal-footer .btn-outline-info:hover:not(:disabled) {
  background-color: #f0f9ff;
  border-color: #008da8;
  color: #008da8;
}

/* Pagination (matching Settings.vue) */
.pagination-arrow {
  margin: 0 8px;
  color: #00a0c1;
  cursor: pointer;
  text-decoration: none;
}
.pagination-arrow.disabled {
  color: #ccc;
  cursor: not-allowed;
  pointer-events: none;
}
.pagination-label {
  display: inline-block;
  min-width: 120px;
  color: #435666;
  font-size: 0.9rem;
}
</style>
